import React, { useEffect, useState } from 'react';
import styles from '../../assets/styles/Setting.module.scss';
import ProjectSettingsLayout from '../../components/Setting/ProjectSettingsLayout';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useUser } from '../../contexts/UserContext';
import { FUNNEL_STAGE_MAP } from '../../utils/constants';
import { AreaTargetType, AreaTarget, AreaType } from '../../types/project';
import { GeoSelector, getFullAreaName } from '../../components/Setting/GeoSelector';

type KpiType = 'MAXIMIZE_CV' | 'LOWER_CPA' | 'MAXIMIZE_ROAS' | 'MAXIMIZE_CLICKS' | 'INCREASE_AWARENESS';

type KpiPriorityItem = {
  kpi_type: KpiType;
  priority_order: number;
};

type PreferredMessageItem = {
  message_text: string;
  message_type: string;
};

type GeoTarget = {
  id: string;
  name: string;
  label: string;
  parent_id?: string;
  target_type: string;
};

const AREA_TYPE_MAP: Record<string, AreaType> = {
  Prefecture: 'prefecture',
  City: 'city',
};

type PersonaResponse = {
  status: string;
  data?: {
    project_id: number;
    site_url: string | null;
    service_name: string | null;
    service_overview: string | null;
    persona_insight: string | null;
    competitive_edge: string | null;
    cv_definition: string | null;
    thumb_rule: string | null;
    target_cpa: number | null;
    keep_ads_conditions: string | null;
    legal_notes: string | null;
    funnel_stage: { [key: string]: boolean } | null;
    kpi_priorities: KpiPriorityItem[];
    target_areas: AreaTarget[];
    preferred_messages: PreferredMessageItem[];
  };
};

const DEFAULT_FUNNEL_STAGE = {
  pre_awareness: false,
  awareness: false,
  research: false,
  comparison: false,
  conversion: false,
  retargeting: false,
};

const SettingPreference: React.FC = () => {
  const { user } = useUser();
  const { selectedProjectId } = useProjectContext();
  const [isGenerating, setIsGenerating] = useState(false);
  const [urlInput, setUrlInput] = useState<string | null>(null);

  // 1. プロジェクトの基本情報
  const [serviceName, setServiceName] = useState<string | null>(null);
  const [serviceOverview, setServiceOverview] = useState<string | null>(null);
  const [personaInsight, setPersonaInsight] = useState<string | null>(null);
  const [competitors, setCompetitors] = useState<string | null>(null);
  const [conversionDef, setConversionDef] = useState<string | null>(null);

  // 2. マーケティングの文脈
  const [funnelStage, setFunnelStage] = useState<{ [key: string]: boolean }>({
    ...DEFAULT_FUNNEL_STAGE,
  });

  const [kpiPriorities, setKpiPriorities] = useState<KpiPriorityItem[]>([]);
  const [kpiInput, setKpiInput] = useState<KpiType>('MAXIMIZE_CV');

  const [preferredMessages, setPreferredMessages] = useState<PreferredMessageItem[]>([]);
  const [appealMessageInput, setAppealMessageInput] = useState<string | null>(null);
  const [avoidMessageInput, setAvoidMessageInput] = useState<string | null>(null);

  // 3. テクニカルな前提条件
  const [targetAreas, setTargetAreas] = useState<AreaTarget[]>([]);
  const [geoTargets, setGeoTargets] = useState<GeoTarget[]>([]);

  useEffect(() => {
    const fetchGeoTargets = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get-geo-targets`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setGeoTargets(data.geo_targets || []);
        }
      } catch (e) {
        console.error("Failed to fetch geo targets:", e);
      }
    };
    fetchGeoTargets();
  }, []);

  const [targetCpa, setTargetCpa] = useState<number | string>('');
  const [keepAdsConditions, setKeepAdsConditions] = useState<string | null>(null);
  const [legalNotes, setLegalNotes] = useState<string | null>(null);
  const [thumbRule, setThumbRule] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const applyPersonaToForm = (persona: NonNullable<PersonaResponse['data']>) => {
    setUrlInput(persona.site_url);
    setServiceName(persona.service_name);
    setServiceOverview(persona.service_overview);
    setPersonaInsight(persona.persona_insight);
    setCompetitors(persona.competitive_edge);
    setConversionDef(persona.cv_definition);
    setFunnelStage({
      ...DEFAULT_FUNNEL_STAGE,
      ...(persona.funnel_stage || {}),
    });
    setKpiPriorities(persona.kpi_priorities || []);
    setPreferredMessages(persona.preferred_messages || []);
    setTargetAreas(persona.target_areas || []);
    setTargetCpa(persona.target_cpa ?? '');
    setKeepAdsConditions(persona.keep_ads_conditions);
    setLegalNotes(persona.legal_notes);
    setThumbRule(persona.thumb_rule);
  };


  const handleResetOnboarding = async () => {
    if (!window.confirm("オンボーディング設定をすべてリセットしますか？この操作は元に戻せません。")) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/reset-onboarding-debug`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: selectedProjectId }),
        credentials: "include"
      });
      if (res.ok) {
        alert("リセットしました。画面をリロードします。");
        window.location.reload();
      } else {
        alert("リセットに失敗しました");
      }
    } catch (e) {
      alert("エラーが発生しました");
    }
  };

  const toNullableValue = (value: string | null) => {

    if (value == null) return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  };

  useEffect(() => {
    const fetchPersona = async () => {
      if (!selectedProjectId) {
        setUrlInput(null);
        setServiceName(null);
        setServiceOverview(null);
        setPersonaInsight(null);
        setCompetitors(null);
        setConversionDef(null);
        setFunnelStage({ ...DEFAULT_FUNNEL_STAGE });
        setKpiPriorities([]);
        setPreferredMessages([]);
        setTargetAreas([]);
        setThumbRule(null);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/get-persona?project_id=${selectedProjectId}`,
          { credentials: 'include' }
        );
        if (!response.ok) throw new Error('Failed to fetch persona');

        const result: PersonaResponse = await response.json();
        const persona = result.data;
        if (!persona) return;
        applyPersonaToForm(persona);
      } catch (error) {
        console.error('Failed to fetch persona:', error);
      }
    };

    fetchPersona();
  }, [selectedProjectId]);

  const handleGenerateFromUrl = async () => {
    if (!urlInput?.trim()) {
      alert("公式サイトURLを入力してください");
      return;
    }

    if (!selectedProjectId) {
      alert("プロジェクトが選択されていません");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/extract-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: selectedProjectId,
          url: urlInput.trim(),
        }),
      });

      const result: PersonaResponse | { error?: string } = await response.json();
      if (!response.ok || !('data' in result) || !result.data) {
        throw new Error(('error' in result && result.error) ? result.error : 'URL解析に失敗しました');
      }

      applyPersonaToForm(result.data);
      window.alert('URL解析結果を反映しました');
    } catch (error) {
      console.error('Failed to generate preference from URL:', error);
      window.alert('URLの解析に失敗しました。GPT連携設定とURLを確認してください。');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleToggleFunnelStage = (stage: string) => {
    setFunnelStage(prev => ({ ...prev, [stage]: !prev[stage] }));
  };

  const handleAddKpi = () => {
    if (!kpiInput) return;
    const newOrder = kpiPriorities.length + 1;
    setKpiPriorities([...kpiPriorities, { kpi_type: kpiInput, priority_order: newOrder }]);
  };

  const handleRemoveKpi = (order: number) => {
    setKpiPriorities(
      kpiPriorities
        .filter(k => k.priority_order !== order)
        .map((k, index) => ({ ...k, priority_order: index + 1 }))
    );
  };

  const handleAddAppeal = () => {
    if (appealMessageInput?.trim()) {
      setPreferredMessages([...preferredMessages, { message_text: appealMessageInput.trim(), message_type: 'APPEAL' }]);
      setAppealMessageInput(null);
    }
  };

  const handleAddAvoid = () => {
    if (avoidMessageInput?.trim()) {
      setPreferredMessages([...preferredMessages, { message_text: avoidMessageInput.trim(), message_type: 'AVOID' }]);
      setAvoidMessageInput(null);
    }
  };

  const handleRemoveMessage = (index: number) => {
    setPreferredMessages(preferredMessages.filter((_, i) => i !== index));
  };

  const handleAddArea = (areaId: string, targetType: AreaTargetType) => {
    if (!areaId) return;

    if (targetAreas.some(a => a.area_id === areaId)) {
      alert("すでに追加されているエリアです");
      return;
    }

    const selectedGeo = geoTargets.find((geo) => geo.id === areaId);
    if (!selectedGeo) return;

    const newArea: AreaTarget = {
      area_id: selectedGeo.id,
      area_name: getFullAreaName(selectedGeo, geoTargets, false),
      area_type: AREA_TYPE_MAP[selectedGeo.target_type] || 'city',
      area_target_type: targetType
    };

    setTargetAreas([...targetAreas, newArea]);
  };

  const handleRemoveArea = (index: number) => {
    setTargetAreas(targetAreas.filter((_, i) => i !== index));
  };

  const getAreaDisplayName = (area: AreaTarget) => {
    if (geoTargets.length > 0) {
      const geo = geoTargets.find((item) => item.id === area.area_id);
      if (geo) {
        return getFullAreaName(geo, geoTargets, false);
      }
    }
    return area.area_name || area.area_id;
  };

  const handlePersonaSave = async () => {
    if (!selectedProjectId) {
      window.alert('広告主が選択されていません');
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/save-persona`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: selectedProjectId,
          site_url: toNullableValue(urlInput),
          service_name: toNullableValue(serviceName),
          service_overview: toNullableValue(serviceOverview),
          persona_insight: toNullableValue(personaInsight),
          competitive_edge: toNullableValue(competitors),
          cv_definition: toNullableValue(conversionDef),
          thumb_rule: toNullableValue(thumbRule),
          target_cpa: targetCpa === '' ? null : Number(targetCpa),
          keep_ads_conditions: toNullableValue(keepAdsConditions),
          legal_notes: toNullableValue(legalNotes),
          funnel_stage: funnelStage,
          kpi_priorities: kpiPriorities,
          target_areas: targetAreas,
          preferred_messages: preferredMessages,
        }),
      });

      if (!response.ok) throw new Error('Failed to save persona');
      window.alert('ペルソナ設定を保存しました');
    } catch (error) {
      console.error('Failed to save persona:', error);
      window.alert('ペルソナ設定の保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };



  const KPI_OPTIONS = [
    { value: 'MAXIMIZE_CV', label: 'コンバージョン数の最大化' },
    { value: 'LOWER_CPA', label: 'CPAの改善・引き下げ' },
    { value: 'MAXIMIZE_ROAS', label: 'ROASの最大化・売上効率改善' },
    { value: 'MAXIMIZE_CLICKS', label: 'クリック数の最大化・サイト流入増' },
    { value: 'INCREASE_AWARENESS', label: '認知度の向上' }
  ];

  return (
    <ProjectSettingsLayout>
      <div className={styles.twoColumnGrid}>
        {user?.is_admin && (
          <div style={{ gridColumn: "1 / -1", textAlign: "right", marginBottom: 10 }}>
            <button
              type="button"
              onClick={handleResetOnboarding}
              style={{ background: '#333', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
            >
              🛠 オンボーディングリセット (Debug)
            </button>
          </div>
        )}
        <div className={styles.leftColumnWide}>

          <div className={`${styles.card} ${styles.cardPrimaryTop}`}>
            <div className={styles.cardHeader}>
              <h3>公式サイトURLから自動生成</h3>
            </div>
            <div className={styles.settingDetailContentWrap}>
              <p className={styles.helpText}>対象サービスのURLを入力すると、AibexのAIがサイトを読み込み、プロジェクトの概要やペルソナ、ターゲット戦略を自動推定します。</p>
              <div className={styles.searchGroup}>
                <input
                  type="url"
                  className={styles.inputField}
                  value={urlInput ?? ''}
                  onChange={(e) => setUrlInput(e.target.value || null)}
                  placeholder="https://example.com"
                  disabled={isGenerating}
                />
                <button type="button" className={styles.actionButton} onClick={handleGenerateFromUrl} disabled={isGenerating}>
                  {isGenerating ? '読み込み中...' : 'AI自動生成'}
                </button>
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>1. プロジェクトの基本情報</h3>
            </div>
            <div className={styles.formGroup}>
              <label>サービス名</label>
              <input type="text" className={styles.inputField} value={serviceName ?? ''} onChange={(e) => setServiceName(e.target.value || null)} placeholder="例: マンション【ハルモシティ】" />
            </div>
            <div className={styles.formGroup}>
              <label>サービス概要</label>
              <textarea className={`${styles.inputField} ${styles.textAreaField}`} value={serviceOverview ?? ''} onChange={(e) => setServiceOverview(e.target.value || null)} rows={3} placeholder="提供しているサービスの内容、特徴など" />
            </div>
            <div className={styles.formGroup}>
              <label>ターゲット属性（ペルソナ・インサイト）</label>
              <div className={styles.inputHint}>年齢・性別だけでなく「どんな悩みを抱えているか」「どんな不満を解消したいか」を記載すると効果的です。</div>
              <textarea className={`${styles.inputField} ${styles.textAreaField}`} value={personaInsight ?? ''} onChange={(e) => setPersonaInsight(e.target.value || null)} rows={4} placeholder="例: 30代ファミリー層。現在の家が手狭になってきたが、都心の生活利便性は落としたくないと考えている..." />
            </div>
            <div className={styles.formGroup}>
              <label>競合他社・自社の優位性</label>
              <textarea className={`${styles.inputField} ${styles.textAreaField}`} value={competitors ?? ''} onChange={(e) => setCompetitors(e.target.value || null)} rows={3} placeholder="比較されやすい競合サービスや、自社が勝っている点" />
            </div>
            <div className={styles.formGroup}>
              <label>コンバージョン（CV）の定義</label>
              <select className={styles.inputField} value={conversionDef ?? ''} onChange={(e) => setConversionDef(e.target.value || null)}>
                <option value="">未設定</option>
                <option value="purchase">購入 / 申し込み</option>
                <option value="inquiry">資料請求 / お問い合わせ</option>
                <option value="visit">来店予約 / 内見予約</option>
                <option value="download">ホワイトペーパー・アプリ等のダウンロード</option>
                <option value="other">その他</option>
              </select>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>2. マーケティングの文脈</h3>
            </div>

            <div className={styles.formGroup}>
              <label>検討フェーズ (配信対象とする層)</label>
              <div className={styles.checkboxGroupRow}>
                {Object.keys(FUNNEL_STAGE_MAP).map((stage) => (
                  <label key={stage} className={styles.customCheckboxLabel}>
                    <input type="checkbox" checked={funnelStage[stage] || false} onChange={() => handleToggleFunnelStage(stage)} />
                    <span>{FUNNEL_STAGE_MAP[stage]}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>プロジェクトの目的（KPI優先度）</label>
              <div className={styles.inputHint}>最優先から順に追加してください。</div>
              <div className={styles.tagInputContainer}>
                <select className={styles.inputField} value={kpiInput} onChange={(e) => setKpiInput(e.target.value as KpiType)}>
                  {KPI_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <button type="button" className={`${styles.actionButton} ${styles.miniButton}`} onClick={handleAddKpi}>追加</button>
              </div>
              <div className={styles.tagList}>
                {kpiPriorities.sort((a, b) => a.priority_order - b.priority_order).map(k => (
                  <span key={k.priority_order} className={styles.tagItem}>
                    {k.priority_order}位: {KPI_OPTIONS.find(o => o.value === k.kpi_type)?.label || k.kpi_type}
                    <button type="button" onClick={() => handleRemoveKpi(k.priority_order)}>×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>USP・優先訴求メッセージ (APPEAL)</label>
              <div className={styles.tagInputContainer}>
                <input type="text" className={styles.inputField} value={appealMessageInput || ''} onChange={(e) => setAppealMessageInput(e.target.value || null)} onKeyDown={(e) => e.key === 'Enter' && handleAddAppeal()} placeholder="訴求したい内容を追加" />
                <button type="button" className={`${styles.actionButton} ${styles.miniButton}`} onClick={handleAddAppeal}>追加</button>
              </div>
              <div className={styles.tagList}>
                {preferredMessages.map((msg, idx) => msg.message_type === 'APPEAL' && (
                  <span key={idx} className={styles.tagItem}>
                    {msg.message_text} <button type="button" onClick={() => handleRemoveMessage(idx)}>×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>NGワード・禁止表現 (AVOID)</label>
              <div className={styles.inputHint}>ブランド毀損を防ぐため、AIに提案させないキーワード。</div>
              <div className={styles.tagInputContainer}>
                <input type="text" className={styles.inputField} value={avoidMessageInput || ''} onChange={(e) => setAvoidMessageInput(e.target.value || null)} onKeyDown={(e) => e.key === 'Enter' && handleAddAvoid()} placeholder="追加するキーワードを入力してEnter" />
                <button type="button" className={`${styles.actionButton} ${styles.miniButton}`} onClick={handleAddAvoid}>追加</button>
              </div>
              <div className={styles.tagList}>
                {preferredMessages.map((msg, idx) => msg.message_type === 'AVOID' && (
                  <span key={idx} className={styles.tagItem}>
                    {msg.message_text} <button type="button" onClick={() => handleRemoveMessage(idx)}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>3. テクニカルな前提条件</h3>
            </div>
            <div className={styles.formGroup}>
              <label>対象・除外エリア設定</label>
              <div className={styles.inputHint}>都道府県・市区町村から対象エリアを選択してください。</div>
              <GeoSelector geoTargets={geoTargets} onAdd={handleAddArea} />
              <div className={styles.tagList}>
                {targetAreas.map((area, idx) => (
                  <span key={idx} className={styles.tagItem}>
                    <span className={area.area_target_type === 'INCLUDE' ? styles.statusActive : styles.statusInactive}>
                      [{area.area_target_type === 'INCLUDE' ? '対象' : '除外'}]&nbsp;
                    </span>
                    {getAreaDisplayName(area)}
                    <button type="button" onClick={() => handleRemoveArea(idx)}>×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>目標CPA</label>
              <div className={styles.inputHint}>改善提案の判断軸となる目標CPA単価（円）</div>
              <input type="number" className={styles.inputField} value={targetCpa} onChange={(e) => setTargetCpa(e.target.value)} placeholder="例: 10000" />
            </div>

            <div className={styles.formGroup}>
              <label>意図的に残す広告の条件</label>
              <div className={styles.inputHint}>認知・ブランド目的等でCPAが悪くても残す広告条件</div>
              <textarea className={`${styles.inputField} ${styles.textAreaField}`} value={keepAdsConditions ?? ''} onChange={(e) => setKeepAdsConditions(e.target.value || null)} rows={2} placeholder="例: 指名検索キーワードは停止しない" />
            </div>

            <div className={styles.formGroup}>
              <label>法的注意点・規約</label>
              <div className={styles.inputHint}>薬機法、景品表示法などの業界制約</div>
              <textarea className={`${styles.inputField} ${styles.textAreaField}`} value={legalNotes ?? ''} onChange={(e) => setLegalNotes(e.target.value || null)} rows={2} placeholder="例: 「絶対」「No.1」などの表現は使用不可" />
            </div>

            <div className={styles.formGroup}>
              <label>季節性やイベントなどの経験則</label>
              <textarea className={`${styles.inputField} ${styles.textAreaField}`} value={thumbRule ?? ''} onChange={(e) => setThumbRule(e.target.value || null)} rows={2} placeholder="例: ボーナス期の12月と6月に売上が伸びる傾向がある" />
            </div>
          </div>

          <div className={styles.formFooter}>
            <button className={`${styles.actionButton} ${styles.actionButtonLarge}`} onClick={handlePersonaSave} disabled={isSaving}>
              {isSaving ? '保存中...' : '設定を保存する'}
            </button>
          </div>
        </div>
      </div>
    </ProjectSettingsLayout>
  );
};

export default SettingPreference;
